import { PaginationPayload } from 'app/shared/pagination/models';
import {
  RuleAdminResponseType,
  RulesAdminInitialState,
  RulesAdminModalType,
} from 'app/modules/rulesAdmin/models';
import { RuleAdminTypes } from 'app/modules/sidebar/models';
import { ReactElement } from 'react';
import { LocalTableConfigType } from 'app/modules/localTableSettings/models';
import { U21TableColumn } from 'app/shared/u21-ui/components';
import {
  IconBellRinging,
  IconCalendarMinus,
  IconCheckupList,
  IconClipboardList,
  IconFlag,
  IconLibrary,
  IconSettings,
  IconZoomQuestion,
} from '@u21/tabler-icons';

export const DEFAULT_PAGINATION_PAYLOAD: PaginationPayload = {
  limit: 25,
  offset: 1,
  sort_column: 'id',
  sort_direction: 'ascending',
};

type RuleAdminColumn = U21TableColumn<RuleAdminResponseType>;

const toTextCol = (id: string, header: string): RuleAdminColumn => ({
  accessor: id,
  cellProps: { ellipsis: true },
  Header: header,
  id,
  type: 'text',
  width: 200,
});

const RULE_ADMIN_VALIDATION_COLUMNS: RuleAdminColumn[] = [
  toTextCol('ruleValidationId', 'Rule validation ID'),
  toTextCol('ruleId', 'Rule ID'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('executionRangeStart', 'Execution range start'),
  toTextCol('executionRangeEnd', 'Execution range end'),
  toTextCol('createdAt', 'Created at'),
  toTextCol('completedAt', 'Completed at'),
  toTextCol('ruleContent', 'Rule content'),
  toTextCol('status', 'Status'),
  toTextCol('progress', 'Progress'),
];

const RULE_ADMIN_QUERY_COLUMNS: RuleAdminColumn[] = [
  toTextCol('ruleQueryId', 'Rule query ID'),
  toTextCol('ruleId', 'Rule ID'),
  toTextCol('ruleValidationId', 'Rule validation ID'),
  toTextCol('ruleExecutionId', 'Rule execution ID'),
  toTextCol('ruleScheduleId', 'Rule schedule ID'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('createdAt', 'Created at'),
  toTextCol('queryString', 'Query string'),
  toTextCol('errorResult', 'Error result'),
  toTextCol('successRowReturned', 'Success row returned'),
  toTextCol('executionStart', 'Execution start'),
  toTextCol('executionEnd', 'Execution end'),
  toTextCol('windowStart', 'Window start'),
  toTextCol('windowEnd', 'Window end'),
  toTextCol('dbEngine', 'DB engine'),
];

const RULE_ADMIN_EXECUTION_COLUMNS: RuleAdminColumn[] = [
  toTextCol('ruleExecutionId', 'Rule execution ID'),
  toTextCol('ruleId', 'Rule ID'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('executionRangeStart', 'Execution range start'),
  toTextCol('executionRangeEnd', 'Execution range end'),
  toTextCol('startTime', 'Start time'),
  toTextCol('endTime', 'End time'),
  toTextCol('executionType', 'Execution type'),
  toTextCol('stats', 'Statistics'),
];

const RULE_ADMIN_SCENARIO_CONFIG_COLUMNS: RuleAdminColumn[] = [
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('enabledScenarios', 'Enabled scenarios'),
];

const RULE_ADMIN_CONTENT_COLUMNS: RuleAdminColumn[] = [
  toTextCol('ruleId', 'Rule ID'),
  toTextCol('title', 'Title'),
  toTextCol('desc', 'Description'),
  toTextCol('deployStartDate', 'Deploy start date'),
  toTextCol('deployEndDate', 'Deploy end date'),
  toTextCol('ruleContent', 'Rule content'),
  toTextCol('status', 'Status'),
  toTextCol('createdById', 'Creator ID'),
  toTextCol('createdByName', 'Creator name'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('scenarioId', 'Scenario ID'),
  toTextCol('scenarioName', 'Scenario name'),
  toTextCol('options', 'Options'),
  toTextCol('groupByTypes', 'Group by types'),
  toTextCol('generatesAlerts', 'Generates alerts'),
  toTextCol('isSynchronous', 'Is synchronous'),
  toTextCol('runIndependently', 'Runs independently'),
  toTextCol('executionFrequency', 'Execution frequency'),
  toTextCol('executionEngine', 'Execution engine'),
  toTextCol('updatedAt', 'Updated at'),
  toTextCol('customerSupportInvolved', 'Customer support involved'),
  toTextCol('templateId', 'Template ID'),
  toTextCol('templateName', 'Template name'),
  toTextCol('alertQueueId', 'Alert queue ID'),
  toTextCol('alertQueueName', 'Alert queue name'),
  toTextCol('activatedAt', 'Activated at'),
  toTextCol('actualCreatedById', 'Actual creator ID'),
  toTextCol('actualCreatedByName', 'Actual creator name'),
  toTextCol('migratedFromId', 'Migrated from ID'),
];

const RULE_ADMIN_SCHEDULE_COLUMNS: RuleAdminColumn[] = [
  toTextCol('scheduleId', 'Schedule ID'),
  toTextCol('ruleId', 'Rule ID'),
  toTextCol('createdAt', 'Created at'),
  toTextCol('windowStart', 'Window start'),
  toTextCol('windowEnd', 'Window end'),
  toTextCol('executionStartedAt', 'Execution started at'),
  toTextCol('executionCompletedAt', 'Execution completed at'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('retryCount', 'Retry count'),
  toTextCol('scheduleStatus', 'Schedule status'),
  toTextCol('scheduleType', 'Schedule type'),
  toTextCol('executionId', 'Execution IDs'),
];

const RULE_ADMIN_RISK_MODEL_EXECUTION_COLUMNS: RuleAdminColumn[] = [
  toTextCol('riskModelExecutionId', 'Risk model execution ID'),
  toTextCol('riskModelId', 'Risk model ID'),
  toTextCol('orgId', 'Org ID'),
  toTextCol('orgName', 'Org name'),
  toTextCol('status', 'Status'),
  toTextCol('executionType', 'Execution type'),
  toTextCol('startTime', 'Start time'),
  toTextCol('endTime', 'End time'),
  toTextCol('statistics', 'Statistics'),
  toTextCol('riskModelName', 'Risk model name'),
  toTextCol('executorId', 'Executor ID'),
  toTextCol('jobRunId', 'Job run ID'),
];

const RULE_ADMIN_RISK_MODEL_COLUMNS: RuleAdminColumn[] = [
  toTextCol('id', 'Risk model ID'),
  toTextCol('title', 'Title'),
  toTextCol('status', 'Status'),
  toTextCol('low_risk_ceiling', 'Low risk ceiling'),
  toTextCol('high_risk_floor', 'High risk floor'),
  toTextCol('org_id', 'Org ID'),
  toTextCol('org_name', 'Org name'),
  toTextCol('updated_at', 'Updated at'),
  toTextCol('options', 'Options'),
  {
    accessor: 'score_remaining_entities',
    id: 'score_remaining_entities',
    Header: 'Score remaining entities',
    type: 'boolean',
    width: 200,
  },
];

const RULE_ADMIN_RISK_MODEL_CONFIG_COLUMNS: RuleAdminColumn[] = [
  toTextCol('risk_model_id', 'Risk model ID'),
  toTextCol('segment_id', 'Segment ID'),
  toTextCol('segment_name', 'Segment name'),
  {
    accessor: 'is_base_segment',
    id: 'is_base_segment',
    Header: 'Base segment',
    type: 'boolean',
    width: 200,
  },
  toTextCol('org_id', 'Org ID'),
  toTextCol('parameter', 'Parameter'),
  toTextCol('weight', 'Weight'),
  toTextCol('metric', 'Metric'),
  toTextCol('metric_type', 'Metric type'),
  toTextCol('unscored_risk', 'Unscored risk'),
  toTextCol('unknown_risk', 'Unknown risk'),
  toTextCol('custom_data_setting_id', 'Custom data setting ID'),
  toTextCol('method', 'Method'),
  toTextCol('content', 'Content'),
  toTextCol('risk', 'Risk'),
  toTextCol('matchlist_id', 'Matchlist ID'),
  {
    accessor: 'default_high',
    id: 'default_high',
    Header: 'Default high',
    type: 'boolean',
    width: 200,
  },
  toTextCol('parameter_args', 'Parameter args'),
];

const RULE_ADMIN_ALERT_CONTENT_COLUMNS: RuleAdminColumn[] = [
  toTextCol('id', 'Id'),
  toTextCol('title', 'Title'),
  toTextCol('description', 'Description'),
  toTextCol('startDate', 'Start date'),
  toTextCol('endDate', 'End date'),
  toTextCol('histogramData', 'Histogram data'),
  toTextCol('status', 'Status'),
  toTextCol('alertType', 'Alert type'),
  toTextCol('assignedToId', 'Assigned to id'),
  toTextCol('ruleExecutionId', 'Rule execution id'),
  toTextCol('orgId', 'Org id'),
  toTextCol('orgName', 'Org name'),
  toTextCol('customData', 'Custom data'),
  toTextCol('externalId', 'External id'),
  toTextCol('content', 'Content'),
  toTextCol('hits', 'Hits'),
  toTextCol('externalStatus', 'External status'),
  toTextCol('investigationData', 'Investigation data'),
  toTextCol('disposition', 'Disposition'),
  toTextCol('assignedAt', 'Assigned at'),
  toTextCol('isInvestigationValidated', 'Is investigation validated'),
  toTextCol('retrievedAt', 'Retrieved at'),
  toTextCol('closingActionTriggerId', 'Closing action trigger id'),
  toTextCol('groupByTypes', 'Group by types'),
  toTextCol('dispositionedAt', 'Dispositioned at'),
  toTextCol('hotStorageExpiresAt', 'Hot storage expires at'),
  toTextCol('workStartedOn', 'Work started on'),
  toTextCol('updatedAt', 'Updated at'),
  toTextCol('queueId', 'Queue id'),
  toTextCol('alertingOrgId', 'Alerting org id'),
  toTextCol('groupByExternalId', 'Group by external id'),
  toTextCol('collaboratingOrgId', 'Collaborating org id'),
  toTextCol('createdById', 'Created by id'),
  toTextCol('narrative', 'Narrative'),
  toTextCol('dispositionType', 'Disposition type'),
];

const RULE_ADMIN_ALERT_HIT_COLUMNS: RuleAdminColumn[] = [
  toTextCol('id', 'Id'),
  toTextCol('createdAt', 'Created at'),
  toTextCol('alertId', 'Alert id'),
  toTextCol('groupByExternalId', 'Group by external id'),
  toTextCol('groupByTypes', 'Group by types'),
  toTextCol('title', 'Title'),
  toTextCol('description', 'Description'),
  toTextCol('startDate', 'Start date'),
  toTextCol('endDate', 'End date'),
  toTextCol('content', 'Content'),
  toTextCol('customData', 'Custom data'),
  toTextCol('alertHitType', 'Alert hit type'),
  toTextCol('ruleExecutionId', 'Rule execution id'),
  toTextCol('orgId', 'Org id'),
  toTextCol('orgName', 'Org name'),
  toTextCol('unitId', 'Unit id'),
  toTextCol('ruleScheduleId', 'Rule schedule id'),
  toTextCol('ruleId', 'Rule id'),
  toTextCol('objectSource', 'Object source'),
  toTextCol('ruleValidationId', 'Rule validation id'),
  toTextCol('ruleValidationWindowId', 'Rule validation window id'),
];

export const rulesAdminInitialState: Readonly<RulesAdminInitialState> = {
  rulesAdminModalType: RulesAdminModalType.NONE,
};

export const RULE_ADMIN_INFO_MAP: Record<
  RuleAdminTypes,
  {
    title: string;
    icon: ReactElement;
    localTableConfigType: LocalTableConfigType;
    defaultColumns: RuleAdminColumn[];
  }
> = {
  [RuleAdminTypes.SCENARIO_CONFIG]: {
    title: 'Scenario Config',
    icon: <IconClipboardList />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_SCENARIO_CONFIG_TABLE,
    defaultColumns: RULE_ADMIN_SCENARIO_CONFIG_COLUMNS,
  },
  [RuleAdminTypes.RULE_CONTENT]: {
    title: 'Rule',
    icon: <IconClipboardList />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_CONTENT_TABLE,
    defaultColumns: RULE_ADMIN_CONTENT_COLUMNS,
  },
  [RuleAdminTypes.RULE_QUERY]: {
    title: 'Rule Query',
    icon: <IconZoomQuestion />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_QUERY_TABLE,
    defaultColumns: RULE_ADMIN_QUERY_COLUMNS,
  },
  [RuleAdminTypes.RULE_EXECUTION]: {
    title: 'Rule Execution',
    icon: <IconSettings />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_EXECUTION_TABLE,
    defaultColumns: RULE_ADMIN_EXECUTION_COLUMNS,
  },
  [RuleAdminTypes.RULE_VALIDATION]: {
    title: 'Rule Validation',
    icon: <IconCheckupList />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_VALIDATION_TABLE,
    defaultColumns: RULE_ADMIN_VALIDATION_COLUMNS,
  },
  [RuleAdminTypes.RULE_SCHEDULE]: {
    title: 'Rule Schedule',
    icon: <IconCalendarMinus />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_SCHEDULE_TABLE,
    defaultColumns: RULE_ADMIN_SCHEDULE_COLUMNS,
  },
  [RuleAdminTypes.RISK_MODEL_EXECUTION]: {
    title: 'Risk Model Execution',
    icon: <IconSettings />,
    localTableConfigType:
      LocalTableConfigType.RULE_ADMIN_RISK_MODEL_EXECUTION_TABLE,
    defaultColumns: RULE_ADMIN_RISK_MODEL_EXECUTION_COLUMNS,
  },
  [RuleAdminTypes.RISK_MODEL]: {
    title: 'Risk Model',
    icon: <IconLibrary />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_RISK_MODEL_TABLE,
    defaultColumns: RULE_ADMIN_RISK_MODEL_COLUMNS,
  },
  [RuleAdminTypes.RISK_MODEL_CONFIG]: {
    title: 'Risk Model Config',
    icon: <IconClipboardList />,
    localTableConfigType:
      LocalTableConfigType.RULE_ADMIN_RISK_MODEL_CONFIG_TABLE,
    defaultColumns: RULE_ADMIN_RISK_MODEL_CONFIG_COLUMNS,
  },
  [RuleAdminTypes.ALERT_CONTENT]: {
    title: 'Alert Content',
    icon: <IconBellRinging />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_ALERT_CONTENT_TABLE,
    defaultColumns: RULE_ADMIN_ALERT_CONTENT_COLUMNS,
  },
  [RuleAdminTypes.ALERT_HIT]: {
    title: 'Alert Hit',
    icon: <IconFlag />,
    localTableConfigType: LocalTableConfigType.RULE_ADMIN_ALERT_HIT_TABLE,
    defaultColumns: RULE_ADMIN_ALERT_HIT_COLUMNS,
  },
};
